import { useSyncExternalStore } from "react";
import { create } from "zustand";

import type { GenerexEnvironment, ServerVersion } from "../api/types";
import { showPreprodHelp } from "../help/topics";

/** Environnement servi (registre F35, docs/11 §11) : la production (https://generex.bellicom.fr), la préproduction
 *  (https://preprod.generex.bellicom.fr) ou le développement local. Lu sur `GET /api/version` avant le premier
 *  affichage (avec le dictionnaire de la langue, 1,5 s au plus), puis tenu à jour par la surveillance de version
 *  (versionCheck.ts). Serveur muet au démarrage : un nom d'hôte qui commence par `preprod.` vaut préproduction, tout
 *  autre vaut production — la valeur sûre, qui ne montre ni bandeau ni bouton « Promouvoir ». En production, les
 *  routes de la promotion n'existent pas sur le serveur : le client ne les appelle jamais. */

export const ENVIRONMENTS: readonly GenerexEnvironment[] = ["production", "preprod", "dev"];
/** Attente maximale de `/api/version` avant le premier affichage. */
export const ENVIRONMENT_TIMEOUT_MS = 1500;

/** Environnement annoncé par le serveur, s'il est connu. */
export function parseEnvironment(value: unknown): GenerexEnvironment | null {
  return typeof value === "string" && (ENVIRONMENTS as readonly string[]).includes(value)
    ? (value as GenerexEnvironment)
    : null;
}

/** Environnement déduit du nom d'hôte, quand le serveur ne répond pas : `preprod.` en tête, sinon production. */
export function environmentFromHost(host: string | null | undefined): GenerexEnvironment {
  return (host ?? "").toLowerCase().startsWith("preprod.") ? "preprod" : "production";
}

interface EnvironmentState {
  environment: GenerexEnvironment;
  /** Version servie par l'API (null tant qu'elle n'a pas répondu). */
  build: string | null;
  /** L'environnement vient du serveur (et non du nom d'hôte). */
  fromServer: boolean;
}

export const useEnvironment = create<EnvironmentState>(() => ({
  environment: "production",
  build: null,
  fromServer: false,
}));

/** Adopte l'environnement (et la version) : l'aide montre la fiche de la promotion en préproduction seulement. */
export function setEnvironment(environment: GenerexEnvironment, build: string | null, fromServer: boolean): void {
  showPreprodHelp(environment === "preprod");
  useEnvironment.setState({ environment, build, fromServer });
}

/** Réponse de `/api/version` relevée par la surveillance de version : environnement et version mis à jour. Une
 *  réponse sans environnement (API d'avant la préproduction, pendant une mise en ligne) ne change rien. */
export function versionSeen(version: Partial<ServerVersion> | null): void {
  const environment = parseEnvironment(version?.environment);
  if (!environment) return;
  setEnvironment(environment, typeof version?.build === "string" ? version.build : null, true);
}

export const isPreprod = (s: Pick<EnvironmentState, "environment">): boolean => s.environment === "preprod";

/** Valeur tirée de l'environnement (`select` rend une valeur simple : booléen, texte), relue à chaque changement.
 *  L'état courant sert aussi au rendu hors navigateur (essais `renderToStaticMarkup`), et non l'état initial. */
export function useEnvironmentValue<T extends string | boolean | null>(select: (s: EnvironmentState) => T): T {
  const read = () => select(useEnvironment.getState());
  return useSyncExternalStore(useEnvironment.subscribe, read, read);
}

/** `GET /api/version` (version et environnement), null si le serveur ne répond pas. */
export async function fetchServerVersion(signal?: AbortSignal): Promise<Partial<ServerVersion> | null> {
  try {
    const res = await fetch("/api/version", { cache: "no-store", signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { build?: unknown; environment?: unknown };
    return {
      build: typeof body.build === "string" ? body.build : undefined,
      environment: parseEnvironment(body.environment) ?? undefined,
    };
  } catch {
    return null; // serveur injoignable ou attente dépassée
  }
}

/** Environnement au démarrage : celui du serveur s'il répond dans le délai, sinon celui du nom d'hôte. */
export async function loadEnvironment(
  fetchVersion: (signal?: AbortSignal) => Promise<Partial<ServerVersion> | null> = fetchServerVersion,
  host: string = typeof window === "undefined" ? "" : window.location.hostname,
  timeoutMs = ENVIRONMENT_TIMEOUT_MS,
): Promise<GenerexEnvironment> {
  const controller = typeof AbortController === "undefined" ? undefined : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(null);
    }, timeoutMs);
  });
  const version = await Promise.race([fetchVersion(controller?.signal).catch(() => null), late]);
  clearTimeout(timer);
  const environment = parseEnvironment(version?.environment);
  if (environment) setEnvironment(environment, typeof version?.build === "string" ? version.build : null, true);
  else setEnvironment(environmentFromHost(host), typeof version?.build === "string" ? version.build : null, false);
  return useEnvironment.getState().environment;
}

/** Titre de l'onglet : « Préproduction · Generex » en préproduction (`label` : « Préproduction » traduit), sinon
 *  « Generex ». */
export function documentTitle(environment: GenerexEnvironment, label: string): string {
  return environment === "preprod" ? `${label} · Generex` : "Generex";
}
